import React from 'react';
import PropTypes from 'prop-types';

import {Alert, MenuItem, Stack, TextField, Typography} from 'views/components';
import {ExpandMore} from 'views/icons';
import {AdminAutocomplete, translate, Translate} from 'views/module';

import getAutomationRules from '../../../util/automationRules';

const AutomationsStepRules = (props) => {
  const {
    automationForm,
    initAssigneesInfo,
    isTriggerLocked,
    setAssigneeRns,
    setPrompt,
    setResourceType,
    setUpdateRuleField,
  } = props;
  const [selectedAdminOption, setSelectedAdminOption] = React.useState(initAssigneesInfo?.[0] || null);

  const automationRules = React.useMemo(() => getAutomationRules(), []);

  React.useEffect(() => {
    if (automationForm.assigneeRns.length === 0) {
      setSelectedAdminOption(null);
    }
  }, [automationForm.assigneeRns]);

  React.useEffect(() => {
    if (!initAssigneesInfo) {
      return;
    }

    setSelectedAdminOption(initAssigneesInfo[0] || null);
  }, [initAssigneesInfo]);

  const handleSelectedAdminChange = React.useCallback((nextSelectedAdmin) => {
    const nextAssigneeRn = nextSelectedAdmin?.userRn;

    setSelectedAdminOption(nextSelectedAdmin);
    setAssigneeRns(nextAssigneeRn ? [nextAssigneeRn] : []);
  }, [setAssigneeRns]);

  const selectedRule = React.useMemo(() => {
    return automationRules.find((rule) => rule.resourceType === automationForm.resourceType) || automationRules[0];
  }, [automationForm.resourceType]);

  const hasUpdateRules = automationForm.updateRules.length > 0;

  const defaultVisibleRequiredFields = React.useMemo(() => {
    return selectedRule.fields.filter((field) => {
      return Boolean(field.isVisibleToAdmin) && Boolean(field.isRequired);
    });
  }, [selectedRule.fields]);

  const defaultStringEqualFieldForSelectedResource = React.useMemo(() => {
    return defaultVisibleRequiredFields.find((field) => field.ruleType === 'STRING_EQUAL') || null;
  }, [defaultVisibleRequiredFields]);

  const defaultPromptField = React.useMemo(() => {
    return defaultVisibleRequiredFields.find((field) => field.ruleType === 'AI_PROMPT') || null;
  }, [defaultVisibleRequiredFields]);

  const stringEqualField = React.useMemo(() => {
    if (isTriggerLocked) {
      return null;
    }

    if (!hasUpdateRules) {
      return defaultStringEqualFieldForSelectedResource;
    }

    const matchingUpdateRule = automationForm.updateRules.find((updateRule) => {
      return updateRule.ruleType === 'STRING_EQUAL';
    });

    if (!matchingUpdateRule) {
      return null;
    }

    return selectedRule.fields.find((field) => {
      return (
        Boolean(field.isVisibleToAdmin) &&
        field.ruleType === 'STRING_EQUAL' &&
        field.key === matchingUpdateRule.stringEqualCheck.key
      );
    });
  }, [automationForm.updateRules, defaultStringEqualFieldForSelectedResource, hasUpdateRules, isTriggerLocked, selectedRule.fields]);

  const promptField = React.useMemo(() => {
    if (isTriggerLocked) {
      return null;
    }

    if (!hasUpdateRules) {
      return defaultPromptField;
    }

    const matchingUpdateRule = automationForm.updateRules.find((updateRule) => {
      return updateRule.ruleType === 'AI_PROMPT';
    });

    if (!matchingUpdateRule) {
      return null;
    }

    return selectedRule.fields.find((field) => {
      return (
        Boolean(field.isVisibleToAdmin) &&
        field.ruleType === 'AI_PROMPT' &&
        field.key === matchingUpdateRule.promptCheck.key
      );
    });
  }, [automationForm.updateRules, defaultPromptField, hasUpdateRules, isTriggerLocked, selectedRule.fields]);

  const valueOptions = React.useMemo(() => {
    if (!stringEqualField) {
      return [];
    }

    return stringEqualField.options || [];
  }, [stringEqualField]);

  const selectedValue = React.useMemo(() => {
    if (isTriggerLocked) {
      return '';
    }

    if (!stringEqualField) {
      return '';
    }

    if (!hasUpdateRules) {
      if (stringEqualField.defaultValue) {
        return stringEqualField.defaultValue;
      }

      if (valueOptions.length === 0) {
        return '';
      }

      return valueOptions[0].value;
    }

    const matchingUpdateRule = automationForm.updateRules.find((updateRule) => {
      return updateRule.ruleType === 'STRING_EQUAL' && updateRule.stringEqualCheck.key === stringEqualField.key;
    });

    return matchingUpdateRule.stringEqualCheck.value;
  }, [automationForm.updateRules, hasUpdateRules, isTriggerLocked, stringEqualField, valueOptions]);

  const selectedAiPrompt = React.useMemo(() => {
    if (isTriggerLocked) {
      return '';
    }

    if (!promptField) {
      return '';
    }

    if (!hasUpdateRules) {
      return '';
    }

    const matchingUpdateRule = automationForm.updateRules.find((updateRule) => {
      return updateRule.ruleType === 'AI_PROMPT' && updateRule.promptCheck.key === promptField.key;
    });

    return matchingUpdateRule.promptCheck.prompt;
  }, [automationForm.updateRules, hasUpdateRules, isTriggerLocked, promptField]);

  return (
    <Stack
      sx={{
        px: {
          xs: '20px',
          sm: '26px',
        },
        py: '18px',
        gap: '20px',
      }}
    >
      <Stack
        sx={{
          gap: '16px',
          p: '20px',
          border: '1px solid #E8E4DE',
          borderRadius: '12px',
          backgroundColor: '#FBFBFB',
        }}
      >
        <Typography
          sx={{
            fontSize: '11px',
            fontWeight: 600,
            color: '#A8A49E',
            lineHeight: '100%',
            textTransform: 'uppercase',
          }}
        >
          <Translate expression='automations.Automations.TriggerConditions.Title' />
        </Typography>
        <Stack
          sx={{
            gap: '10px',
          }}
        >
          {
            isTriggerLocked &&
            <Alert severity='warning'>
              <Translate expression='automations.Automations.TriggerConditions.UnsupportedRuleMessage' />
            </Alert>
          }
          {
            !isTriggerLocked &&
            <Stack
              direction='row'
              alignItems='center'
              flexWrap='wrap'
              sx={{
                gap: '10px',
              }}
            >
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 400,
                  color: '#1A1916',
                  lineHeight: '150%',
                }}
              >
                <Translate expression='automations.Automations.New.Steps.Rules.TriggerConditions.EventResourcePrepend' />
              </Typography>
              <TextField
                select
                value={automationForm.resourceType}
                onChange={setResourceType}
                SelectProps={{
                  IconComponent: ExpandMore,
                  renderValue: (value) => {
                    return automationRules.find((rule) => rule.resourceType === value).resourceTypeLabel;
                  },
                }}
                sx={{
                  'width': 'fit-content',
                  'maxWidth': '100%',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    backgroundColor: '#FFFFFF',
                  },
                  '& .MuiSelect-select': {
                    pl: '16px',
                    pr: '24px',
                    py: '12px',
                    fontSize: '14px',
                    fontWeight: 500,
                    lineHeight: '150%',
                    color: '#DA7955',
                    boxSizing: 'border-box',
                  },
                  '& .MuiSelect-icon': {
                    color: '#DA7955',
                    right: '12px',
                  },
                }}
              >
                {
                  automationRules.map((rule) => (
                    <MenuItem
                      key={rule.resourceType}
                      value={rule.resourceType}
                    >
                      {rule.resourceTypeLabel}
                    </MenuItem>
                  ))
                }
              </TextField>
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 400,
                  color: '#1A1916',
                  lineHeight: '150%',
                }}
              >
                {selectedRule.sentenceConnector}
              </Typography>
              {
                stringEqualField &&
                <TextField
                  select
                  value={selectedValue}
                  onChange={(nextValue) => setUpdateRuleField(stringEqualField, nextValue)}
                  SelectProps={{
                    IconComponent: ExpandMore,
                    renderValue: (value) => {
                      return valueOptions.find((option) => option.value === value)?.label || value;
                    },
                  }}
                  sx={{
                    'width': 'fit-content',
                    'maxWidth': '100%',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      backgroundColor: '#FFFFFF',
                    },
                    '& .MuiSelect-select': {
                      pl: '16px',
                      pr: '24px',
                      py: '12px',
                      fontSize: '14px',
                      fontWeight: 500,
                      lineHeight: '150%',
                      color: '#DA7955',
                      boxSizing: 'border-box',
                    },
                    '& .MuiSelect-icon': {
                      color: '#DA7955',
                      right: '12px',
                    },
                  }}
                >
                  {
                    valueOptions.map((valueOption) => (
                      <MenuItem
                        key={valueOption.value}
                        value={valueOption.value}
                      >
                        {valueOption.label}
                      </MenuItem>
                    ))
                  }
                </TextField>
              }
            </Stack>
          }
        </Stack>
        {
          !isTriggerLocked && promptField &&
          <Stack sx={{gap: '8px'}}>
            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: 600,
                color: '#A8A49E',
                lineHeight: '100%',
                textTransform: 'uppercase',
              }}
            >
              {promptField.label}
            </Typography>
            <TextField
              value={selectedAiPrompt}
              onChange={(nextValue) => setUpdateRuleField(promptField, nextValue)}
              placeholder={translate('automations.Automations.New.Steps.Rules.AiEvaluatedRulePlaceholder')}
              multiline
              minRows={4}
              maxRows={8}
              sx={{
                '& .MuiOutlinedInput-root': {
                  'borderRadius': '12px',
                  'backgroundColor': '#FFFFFF',
                  'alignItems': 'flex-start',
                },
                '& .MuiOutlinedInput-input': {
                  fontSize: '14px',
                  fontWeight: 400,
                  lineHeight: '150%',
                  color: '#1A1916',
                  boxSizing: 'border-box',
                },
                '& .MuiOutlinedInput-input::placeholder': {
                  color: '#A8A49E',
                  opacity: 1,
                  whiteSpace: 'pre-line',
                },
              }}
            />
          </Stack>
        }
      </Stack>

      <Stack sx={{gap: '8px'}}>
        <Typography
          sx={{
            fontSize: '12px',
            fontWeight: 600,
            color: '#A8A49E',
            lineHeight: '100%',
            textTransform: 'uppercase',
          }}
        >
          <Translate expression='automations.Automations.New.Steps.Rules.PromptTitle' />
        </Typography>
        <TextField
          value={automationForm.prompt}
          onChange={setPrompt}
          placeholder={translate('automations.Automations.New.Steps.Rules.PromptPlaceholder')}
          multiline
          minRows={5}
          maxRows={10}
          sx={{
            '& .MuiOutlinedInput-root': {
              'borderRadius': '12px',
              'backgroundColor': '#FFFFFF',
              'alignItems': 'flex-start',
            },
            '& .MuiOutlinedInput-input': {
              fontSize: '14px',
              fontWeight: 400,
              lineHeight: '150%',
              color: '#1A1916',
              boxSizing: 'border-box',
            },
            '& .MuiOutlinedInput-input::placeholder': {
              color: '#A8A49E',
              opacity: 1,
              whiteSpace: 'pre-line',
            },
          }}
        />
      </Stack>

      <Stack sx={{gap: '8px'}}>
        <Typography
          sx={{
            fontSize: '12px',
            fontWeight: 600,
            color: '#A8A49E',
            lineHeight: '100%',
            textTransform: 'uppercase',
          }}
        >
          <Translate expression='automations.Automations.New.Steps.Rules.AssignedToTitle' />
        </Typography>
        <AdminAutocomplete
          selectedAdmin={selectedAdminOption}
          setSelectedAdmin={handleSelectedAdminChange}
        />
      </Stack>
    </Stack>
  );
};

AutomationsStepRules.propTypes = {
  automationForm: PropTypes.shape({
    assigneeRns: PropTypes.arrayOf(PropTypes.string).isRequired,
    prompt: PropTypes.string.isRequired,
    resourceType: PropTypes.string.isRequired,
    updateRules: PropTypes.array.isRequired,
  }).isRequired,
  isTriggerLocked: PropTypes.bool.isRequired,
  setAssigneeRns: PropTypes.func.isRequired,
  setPrompt: PropTypes.func.isRequired,
  setResourceType: PropTypes.func.isRequired,
  setUpdateRuleField: PropTypes.func.isRequired,
  initAssigneesInfo: PropTypes.array,
};

export default React.memo(AutomationsStepRules);
